import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import type { MealEstimate } from "../lib/types";
import type { Goals } from "../lib/goals";
import type { WaterLog } from "../lib/water";
import CalorieRing from "./CalorieRing";
import MacroBars from "./MacroBars";
import WaterTracker from "./WaterTracker";

interface Props {
  totals: MealEstimate["totals"];
  goals: Goals;
  mealCount: number;
  streak: number;
  water: WaterLog[];
  onWaterChange: () => void;
}

// Streak shows only after day 3 — a "🔥 1 day" streak on the very first save
// reads as cheap gamification (UI audit High #7). At 3+ days the user has
// earned the badge.
const MIN_STREAK_TO_SHOW = 3;

export default function DailyDashboard({ totals, goals, mealCount, streak, water, onWaterChange }: Props) {
  const navigate = useNavigate();
  return (
    <div className="mx-4 p-5 rounded-2xl bg-slate-900 border border-slate-800">
      <div className="flex items-center justify-between mb-4 text-[11px] uppercase tracking-wider text-slate-400">
        <span>{mealCount} {mealCount === 1 ? "entry" : "entries"}</span>
        {streak >= MIN_STREAK_TO_SHOW && (
          <span className="flex items-center gap-1 normal-case tracking-normal text-xs">
            <span className="text-amber-400" aria-hidden="true">🔥</span>
            <span className="text-slate-200 font-semibold tabular-nums">{streak}</span>
            <span className="text-slate-400">day{streak === 1 ? "" : "s"} streak</span>
          </span>
        )}
      </div>
      {/* Ring is presentational — wrapping it in a button made the whole 184px
          target an unlabeled jump to Settings (audit Critical #2). Now there's
          a small labeled pill underneath for the explicit edit action. */}
      <div className="flex items-center justify-center w-full mb-3">
        <CalorieRing consumed={totals.calories} goal={goals.calories} />
      </div>
      <div className="flex justify-center mb-5">
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 text-slate-300 text-xs font-medium hover:bg-slate-700 active:bg-slate-700/80"
          aria-label="Edit calorie + macro goals"
        >
          <Pencil className="w-3 h-3" />
          Edit goals
        </button>
      </div>
      <MacroBars totals={totals} goals={goals} />
      <WaterTracker todayLogs={water} onChange={onWaterChange} />
    </div>
  );
}
